import React from 'react';
import { Center, Stack, Text, LoadingOverlay, Box } from '@mantine/core';
import type { LoadingPattern } from '../../theme/mantineTheme';
import { TABLE_SKELETON_ROWS } from '../../theme/mantineTheme';

interface LoadingStateProps {
    /** Which visual pattern to render. */
    variant: LoadingPattern;
    /** Optional label displayed below the spinner (centered variant only). */
    label?: string;
    /** Number of skeleton rows (skeleton variant only). Defaults to TABLE_SKELETON_ROWS. */
    rows?: number;
    /** Loader size (centered variant). */
    size?: number | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** Minimum height for the container (centered variant). */
    minHeight?: number | string;
    /** Whether loading is active (overlay variant only). */
    visible?: boolean;
}

/**
 * Unified loading state component.
 *
 * - **centered**: Branded dots animation with optional label.
 * - **skeleton**: Branded dots centered in a container area.
 * - **overlay**: Semi-transparent overlay with blur over existing content.
 */
export function LoadingState({
    variant,
    label = 'Loading…',
    rows: _rows = TABLE_SKELETON_ROWS,
    size: _size = 'md',
    minHeight = 200,
    visible = true,
}: LoadingStateProps) {
    if (variant === 'centered' || variant === 'skeleton') {
        return (
            <Box className="hades-table-loader" mih={minHeight}>
                <Box className="hades-loader-dots">
                    <span /><span /><span />
                </Box>
                {label && (
                    <Text size="sm" c="dimmed" mt="sm">{label}</Text>
                )}
            </Box>
        );
    }

    if (variant === 'overlay') {
        return (
            <LoadingOverlay
                visible={visible}
                overlayProps={{ radius: 'md', blur: 2 }}
                loaderProps={{ type: 'dots' }}
            />
        );
    }

    return null;
}
